import { IBasePickerSuggestionsProps, ITag } from '@fluentui/react';

//store 
import * as MedicalInsuranceStore from '../../../../redux/middleware/MedicalInsuranceMiddleware';
import * as MedicalServicesStore from '../../../../redux/middleware/MedicalServicesMiddleware';
import * as MedicalInsurancePlanStore from '../../../../redux/middleware/MedicalInsurancePlansMiddleware';
import * as PractitionerAssociationStore from '../../../../redux/middleware/PractitionerMedicalServiceAssociationMiddleware';

// models
import { ErrorType } from '../../../../models/HttpError';
import { AppState } from '../../../../redux/reducers';
import { Practitioner } from '../../../../models/Entities/Practitioners/Practitioner';
import { AvailableMedicalService } from '../../../../models/Entities/MedicalServices/AvailableMedicalService';
import { PractitionerAssociationsMedicalService } from '../../../../models/Entities/PractitionerAssociations/MedicalServices/PractitionerAssociationMedicalService';

interface IProps {
  onSelectedServices: (services: AvailableMedicalService[]) => void;
  MedicalServicesSelected: number[] | undefined;
  MultiselectEnable: boolean;
  noteText?: string;
  PractitionerId: string;
}

interface ConnectedProps {
  isLoading: boolean;
  isUpdating: boolean;
  isUpdatedSuccessfully: boolean;
  isLoadingSuccessfully: boolean;
  failOnUpdating: boolean;
  failOnLoading: boolean;
  error: ErrorType | undefined;
  MedicalServices: AvailableMedicalService[];
  MedicalServicesOptions: ITag[];
  PractitionerMedicalServices: PractitionerAssociationsMedicalService[];
  SelectedMedicalServices: AvailableMedicalService[];
}

interface DispatchProps {
  GetAllMedicalServices: typeof MedicalServicesStore.actionCreators.GetAllMedicalServices;
  GetMedicalServiceByPractitionerId: typeof PractitionerAssociationStore.actionCreators.GetMedicalServiceByPractitionerId;
}

export type Props = IProps & ConnectedProps & DispatchProps;

/**
 * Prácticas del médico: las del practitioner en el listado y, si el listado todavía no las tiene (o no tiene al
 * practitioner), las de la última carga de asociaciones, siempre que sean de este médico.
 */
export const PractitionerMedicalServicesFromState = (
  state: Pick<AppState, 'Practitioners' | 'PractitionerMedicalServiceAssociations'>,
  practitionerId: string | undefined
): PractitionerAssociationsMedicalService[] | undefined => {
  const id = (practitionerId ?? '').toLowerCase();
  const fromPractitioner = state.Practitioners?.list?.find((item: Practitioner) => item.userId?.toLowerCase() === id)?.MedicalServicesAssociated;
  if (fromPractitioner?.length) return fromPractitioner;
  const fromAssociations = state.PractitionerMedicalServiceAssociations?.list?.filter(
    (association: PractitionerAssociationsMedicalService) => association.practitionerId?.toLowerCase() === id
  );
  return fromAssociations?.length ? fromAssociations : fromPractitioner;
};

export const mapStateToProps = (state: AppState, ownProps: IProps) => ({
  isLoading: state.ContractConcept?.isLoadingAll,
  isUpdating: state.ContractConcept?.isLoadingAll,
  isUpdatedSuccessfully: state.ContractConcept?.successLoadingAll,
  isLoadingSuccessfully: state.ContractConcept?.successLoadingAll,
  failOnUpdating: state.ContractConcept?.failOnLoadingAll,
  failOnLoading: state.ContractConcept?.failOnLoadingAll,
  isDeleting: state.ContractConcept?.isDeletingOne,
  isDeletedSuccessfully: state.ContractConcept?.successDeletingOne,
  failOnDeleting: state.ContractConcept?.failOnDeletingOne,
  error: state.ContractConcept?.error,
  MedicalServices: state.MedicalServices?.list,
  MedicalServicesOptions: GetMedicalServicesOptions(
    PractitionerMedicalServicesFromState(state, ownProps.PractitionerId),
    state.MedicalServices?.list
  ),
  PractitionerMedicalServices: PractitionerMedicalServicesFromState(state, ownProps.PractitionerId),
  SelectedMedicalServices: ownProps.MedicalServicesSelected ? state.MedicalServices?.list?.filter((service: AvailableMedicalService) => ownProps.MedicalServicesSelected?.includes(service.id)) as AvailableMedicalService[] : []
});

export const mapDispatchToProps = ({
  ...MedicalInsuranceStore.actionCreators,
  ...MedicalInsurancePlanStore.actionCreators,
  ...MedicalServicesStore.actionCreators,
  ...PractitionerAssociationStore.actionCreators
});

/**
 * Servicio médico de una práctica del médico: el que viene embebido en la asociación y, si falta, el del catálogo.
 * El catálogo se carga paginado, así que no siempre contiene todas las prácticas del médico.
 */
export const ResolveAssociatedMedicalService = (
  association: PractitionerAssociationsMedicalService | undefined,
  MedicalServices: AvailableMedicalService[] | undefined
): AvailableMedicalService | undefined => {
  const embedded = association?.medicalService;
  const id = embedded?.id ?? (association as { medicalServiceId?: number } | undefined)?.medicalServiceId;
  if (id === undefined || id === null) return undefined;
  const fromCatalogue = MedicalServices?.find((service: AvailableMedicalService) => service.id === id);
  if (embedded?.serviceNameReference) return embedded;
  return fromCatalogue ?? embedded ?? undefined;
};

/** Opciones del selector: una por práctica del médico, siempre con nombre (las sin servicio resoluble se omiten). */
export const GetMedicalServicesOptions = (
  PractitionerMedicalServices: PractitionerAssociationsMedicalService[] | undefined,
  MedicalServices: AvailableMedicalService[] | undefined
): ITag[] => {
  return (PractitionerMedicalServices ?? [])
    .map((item: PractitionerAssociationsMedicalService) => ResolveAssociatedMedicalService(item, MedicalServices))
    .filter((service): service is AvailableMedicalService => !!service)
    .map((service) => ({ key: service.id, name: service.serviceNameReference || `#${service.id}` }));
};

/** Texto comparable: minúsculas y sin acentos ("cardiológica" ⇔ "cardiologica"). */
const searchable = (value: string | undefined | null): string =>
  (value ?? '').normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();

/**
 * Sugerencias del TagPicker: las opciones cuyo nombre contiene el texto (en cualquier posición, sin acentos),
 * sin las ya elegidas. Nunca rompe por un nombre vacío.
 */
export const FilterSuggestedMedicalServices = (filterText: string, options: ITag[] | undefined, tagList: ITag[] | undefined): ITag[] => {
  const text = searchable(filterText).trim();
  if (!text) return [];
  return (options ?? []).filter((tag) => searchable(tag.name).includes(text) && !listContainsTagList(tag, tagList));
};

export const getTextFromItem = (item: ITag) => item.name ?? '';

export const listContainsTagList = (tag: ITag, tagList?: ITag[]) => {
  if (!tagList || !tagList.length || tagList.length === 0) {
    return false;
  }
  return tagList.some((compareTag) => compareTag.key === tag.key);
};

export const GetMedicalServiceById = (
  medicalServiceId: number,
  medicalServices: AvailableMedicalService[]
): AvailableMedicalService => {

  const medicalService: AvailableMedicalService = medicalServices?.find(
    (item: AvailableMedicalService) => item.id === medicalServiceId
  ) as AvailableMedicalService;
  
  return medicalService;
};

export function GetSelectedMedicalService(
  medicalServicesId: number[],
  medicalServices: AvailableMedicalService[]
): ITag[] {
  const medicalServicesOptions: ITag[] = [];

  medicalServicesId.forEach((id) => {
    var currentMedicalService = medicalServices.find((service: AvailableMedicalService) => service.id === id);

    if (currentMedicalService) {
      medicalServicesOptions.push({
        key: currentMedicalService.id,
        name: currentMedicalService.serviceNameReference
      } as ITag);
    }
  });
  return medicalServicesOptions;
};

export const pickerSuggestionsProps: IBasePickerSuggestionsProps = {
  suggestionsHeaderText: 'servicios sugeridos',
  noResultsFoundText: 'No se encontro servicio'
};
